import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import * as temporaryLicensesService from '../services/temporaryLicenses';
import { userConfigsSelectors } from './userConfigs';
import { entitiesAdapter } from './common/adapter';

const initialState = {
  isFetching: false,
  isCreating: false,
  isUpdating: false,
  isAssignmentInProcess: false,

  creatingUpdatingError: null,
  fetchingError: null,
  assignmentError: null,
  downloadError: null,
};

const sliceName = 'temporaryLicenses';

export const fetchAll = createAsyncThunk(
  `${sliceName}/fetchAll`,
  (args, thunkAPI) => {
    return temporaryLicensesService.getTemporaryLicenses()
      .catch((error) => {
        error.clientMessage = "Can't get temporaryLicenses from server";
        return thunkAPI.rejectWithValue(error)
      });
  }
);

export const fetchById = createAsyncThunk(
  `${sliceName}/fetchById`,
  (args, thunkAPI) => {
    return temporaryLicensesService.getTemporaryLicenseById(args.id)
      .catch((error) => {
        error.clientMessage = "Can't get temporaryLicense from server";
        return thunkAPI.rejectWithValue(error)
      });
  }
);

export const create = createAsyncThunk(
  `${sliceName}/create`,
  (args, thunkAPI) => {
    return temporaryLicensesService.createNewTemporaryLicenses(
      args.clientId, args.productName, args.type, args.features, args.term, args.licenseCount, args.obtainingMethod,
    )
      .catch((error) => {
        error.clientMessage = "Can't post temporaryLicenses to server";
        return thunkAPI.rejectWithValue(error)
      });
  }
);

export const update = createAsyncThunk(
  `${sliceName}/update`,
  (args, thunkAPI) => {
    return temporaryLicensesService.updateTemporaryLicense(
      args.licenseId, args.features, args.term
    )
      .catch((error) => {
        error.clientMessage = "Can't update temporaryLicenses on server";
        return thunkAPI.rejectWithValue(error)
      });
  }
);

export const deactivate = createAsyncThunk(
  `${sliceName}/deactivate`,
  (args, thunkAPI) => {
    return temporaryLicensesService
      .deactivateTemporaryLicense(args.licenseId)
      .catch((error) => {
        error.clientMessage = "Can't deactivate temporaryLicenses on server";
        return thunkAPI.rejectWithValue(error)
      });
  }
);

export const fetchByKey = createAsyncThunk(
  `${sliceName}/fetchByKey`,
  (args, thunkAPI) => {
    return temporaryLicensesService
      .getTemporaryLicenseByKey(args.licenseKey)
      .catch((error) => {
        error.clientMessage = "Can't get temporaryLicense from server";
        return thunkAPI.rejectWithValue(error)
      });
  }
);


export const assignToClient = createAsyncThunk(
  `${sliceName}/assignToClient`,
  (args, thunkAPI) => {
    return temporaryLicensesService
      .assignLicenseToClient(args.licenseId, args.clientId)
      .catch((error) => {
        error.clientMessage = "Can't get temporaryLicense from server";
        return thunkAPI.rejectWithValue(error)
      });
  }
);


export const activate = createAsyncThunk(
  `${sliceName}/activate`,
  (args, thunkAPI) => {
    return temporaryLicensesService
      .activateLicense(args.licenseId, args.deviceKey)
      .catch((error) => {
        error.clientMessage = "Can't activate temporaryLicense";
        return thunkAPI.rejectWithValue(error)
      });
  }
);

export const download = createAsyncThunk(
  `${sliceName}/download`,
  (args, thunkAPI) => {
    return temporaryLicensesService
      .downloadTemporaryLicense(args.licenseId)
      .catch((error) => {
        error.clientMessage = "Can't download temporaryLicenses";
        return thunkAPI.rejectWithValue(error)
      });
  }
);

export const addToFavorites = createAsyncThunk(
  `${sliceName}/favoritesAdd`,
  (args) => {
    return temporaryLicensesService.addTemporaryLicenseToFavorites(args.id)
  }
);
export const removeFromFavorites = createAsyncThunk(
  `${sliceName}/favoritesRemove`,
  (args) => {
    return temporaryLicensesService.removeTemporaryLicenseFromFavorites(args.id)
  }
);

const temporaryLicenses = createSlice({
  name: sliceName,
  initialState: entitiesAdapter.getInitialState(initialState),
  reducers: {},
  extraReducers: {
    [fetchAll.fulfilled]: (state, action) => {
      state.isFetching = false;
      entitiesAdapter.setAll(state, action);
    },
    [fetchById.fulfilled]: (state, action) => {
      state.isFetching = false;
      entitiesAdapter.upsertOne(state, action);
    },
    [create.fulfilled]: (state, action) => {
      state.isCreating = false;
      entitiesAdapter.upsertMany(state, action);
    },
    [update.fulfilled]: (state, action) => {
      state.isUpdating = false;
      entitiesAdapter.upsertOne(state, action);
    },
    [deactivate.fulfilled]: (state, action) => {
      state.isUpdating = false;
      entitiesAdapter.upsertOne(state, action);
    },
    [fetchByKey.fulfilled]: (state, action) => {
      state.isFetching = false;
      entitiesAdapter.upsertOne(state, action);
    },
    [assignToClient.fulfilled]: (state, action) => {
      state.isAssignmentInProcess = false;
      entitiesAdapter.upsertOne(state, action);
    },
    [activate.fulfilled]: (state, action) => {
      state.isUpdating = false;
      entitiesAdapter.upsertOne(state, action);
    },
    [addToFavorites.fulfilled]: entitiesAdapter.upsertOne,
    [removeFromFavorites.fulfilled]: entitiesAdapter.upsertOne,

    [fetchAll.pending]: (state) => {
      state.isFetching = true;
      state.fetchingError = null;
      state.creatingUpdatingError = null;
    },
    [fetchById.pending]: (state) => {
      state.isFetching = true;
      state.fetchingError = null;
    },
    [create.pending]: (state) => {
      state.isCreating = true;
      state.creatingUpdatingError = null;
    },
    [update.pending]: (state) => {
      state.isUpdating = true;
      state.creatingUpdatingError = null;
    },
    [deactivate.pending]: (state) => {
      state.isUpdating = true;
      state.creatingUpdatingError = null;
    },
    [fetchByKey.pending]: (state) => {
      state.isFetching = true;
      state.fetchingError = null;
    },
    [assignToClient.pending]: (state) => {
      state.isAssignmentInProcess = true;
      state.assignmentError = null;
    },
    [activate.pending]: (state) => {
      state.isUpdating = true;
      state.creatingUpdatingError = null;
    },
    [download.pending]: (state) => {
      state.downloadError = null;
    },

    [fetchAll.rejected]: (state, action) => {
      state.isFetching = false;
      state.fetchingError = action.payload;
    },
    [fetchById.rejected]: (state, action) => {
      state.isFetching = false;
      state.fetchingError = action.payload;
    },
    [create.rejected]: (state, action) => {
      state.isCreating = false;
      state.creatingUpdatingError = action.payload;
    },
    [update.rejected]: (state, action) => {
      state.isUpdating = false;
      state.creatingUpdatingError = action.payload;
    },
    [deactivate.rejected]: (state, action) => {
      state.isUpdating = false;
      state.creatingUpdatingError = action.payload;
    },
    [fetchByKey.rejected]: (state, action) => {
      state.isFetching = false;
      state.fetchingError = action.payload;
    },
    [assignToClient.rejected]: (state, action) => {
      state.isAssignmentInProcess = false;
      state.assignmentError = action.payload;
    },
    [activate.rejected]: (state, action) => {
      state.isUpdating = false;
      state.creatingUpdatingError = action.payload;
    },
    [download.rejected]: (state, action) => {
      state.downloadError = action.payload;
    },
  },
});

const { reducer } = temporaryLicenses;

const temporaryLicensesAdapterSelectors = entitiesAdapter.getSelectors((state) => state[sliceName]);

const selectedLicensesSelector = createSelector(
  (state) => userConfigsSelectors.selectedItems(state, sliceName),
  (state) => temporaryLicensesSelectors.selectEntities(state),
  (selectedIds, entities) => selectedIds.map((id) => entities[id]),
)

export const temporaryLicensesSelectors = {
  ...temporaryLicensesAdapterSelectors,
  isFetching: (state) => state[sliceName].isFetching,
  isCreating: (state) => state[sliceName].isCreating,
  isUpdating: (state) => state[sliceName].isUpdating,
  isAssignmentInProcess: (state) => state[sliceName].isAssignmentInProcess,

  fetchingError: (state) => state[sliceName].fetchingError,
  creatingUpdatingError: (state) => state[sliceName].creatingUpdatingError,
  assignmentError: (state) => state[sliceName].assignmentError,
  downloadError: (state) => state[sliceName].downloadError,

  state: (state) => state[sliceName],
  selectedLicenses: selectedLicensesSelector,
};

export default reducer;



// WEBPACK FOOTER //
// ./src/store/temporaryLicensesSlice.js